'use client';

import React, { useState, useEffect, useRef } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  Col, Container, Form, FormFeedback, FormGroup, Input, Label, Row,
} from 'reactstrap';

// Components
import FeatherIcon from 'feather-icons-react';
import ErrorMessage from '../components/Shared/ErrorMessage';

// Services
import { sendMetrics } from '../../services/ga.service';
import { registerAction } from '../../lib/features/auth/authSlice';
import { useAppDispatch } from '../../lib/hooks';
import { getGeoIp } from '../../services/users.service';
import { getOrCreateSessionId } from '../../services/tracking.service';
import { detectTimezone } from '../../services/timezone.service';
import { safeReturnPath, rememberReturnPath, authFlowOf } from '../../utils/returnPath';

// import images
import shape from '../../assets/images/shape01.png';
import logoIcon from '../../assets/images/logo-icon.png';

// Import constants
// import { BASE_TITLE, GLOBAL_URL } from '../../config/constant';

// Validation functions
const isValidName = (name) => name.length > 0;
const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);
const isValidPassword = (pass) => pass.length >= 6;

// `sign_up_common_error` used to carry no parameters at all, so 32 users failing to
// register were one undifferentiated blob. Map the backend's message onto a stable
// machine code — the raw text is not safe as a GA4 dimension and changes with copy.
const signupErrorReason = (message = '') => {
  const text = String(message).toLowerCase();
  if (text.includes('disposable')) return 'disposable_email';
  if (text.includes('already exist')) return 'email_already_exists';
  if (text.includes('captcha')) return 'captcha_failed';
  if (text.includes('password')) return 'password_rejected';
  if (text.includes('email')) return 'email_rejected';
  return 'unknown';
};

const AuthSignup = () => {
  const [message, setMessage] = useState(null);
  const [ip, setIp] = useState('');
  const [credentials, setCredentials] = useState({
    surname: '',
    name: '',
    password: '',
    email: '',
  });
  const [nInvalid, setNinvalid] = useState(false);
  const [sInvalid, setSinvalid] = useState(false);
  const [eInvalid, setEinvalid] = useState(false);
  const [pInvalid, setPinvalid] = useState(false);
  const [subError, setSubError] = useState('');
  const [loader, setLoader] = useState(false);
  const [buttonTxt, setButtonTxt] = useState('Register');
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);
  // Where the visitor was headed before sign-up (e.g. an OAuth consent page).
  const from = safeReturnPath(useSearchParams().get('from'));

  const route = useRouter();
  const dispatch = useAppDispatch();

  useEffect(() => {
    setMessage(null);
    const asyncget = async () => {
      const ip = await getGeoIp();
      setIp(ip.IPv4);
    };
    asyncget();
  }, []);

  // Save credentials in state
  const onHandleChange = (e) => {
    const { id, value } = e.target;
    switch (id) {
    case 'name':
      if (isValidName(value)) { setNinvalid(false); }
      break;
    case 'surname':
      if (isValidName(value)) { setSinvalid(false); }
      break;
    case 'email':
      if (isValidEmail(value)) { setEinvalid(false); }
      break;
    case 'password':
      if (isValidPassword(value)) { setPinvalid(false); } break;
    default:
    }
    setCredentials({
      ...credentials,
      [id]: value,
    });
  };

  const nameOnBlur = () => (!isValidName(credentials.name) && setNinvalid(true));
  const surnameOnBlur = () => (!isValidName(credentials.surname) && setSinvalid(true));
  const emailOnBlur = () => (!isValidEmail(credentials.email) && setEinvalid(true));
  const passOnBlur = () => (!isValidPassword(credentials.password) && setPinvalid(true));

  const onClear = () => (setSubError(''));

  const onSubmit = async (e) => {
    e.preventDefault();
    const {
      name, surname, email, password,
    } = credentials;
    if (!isValidName(name)
      || !isValidName(surname)
      || !isValidEmail(email)
      || !isValidPassword(password)) {
      // Client-side rejections never reached GA at all, so a form the user simply
      // could not submit looked identical to a form nobody tried to submit.
      const missing = [
        !isValidName(name) && 'name',
        !isValidName(surname) && 'surname',
        !isValidEmail(email) && 'email',
        !isValidPassword(password) && 'password',
      ].filter(Boolean);
      sendMetrics('sign_up_common_error', {
        error_code: 0,
        error_reason: 'client_validation',
        field: missing.join(','),
      });
      setSubError('Fill all required fields');
      return;
    }
    if (!captchaToken) {
      sendMetrics('sign_up_common_error', {
        error_code: 0,
        error_reason: 'captcha_missing',
        field: 'captcha',
      });
      setSubError('Please complete the captcha');
      return;
    }
    setLoader(true);
    setButtonTxt('Sending email...');
    let regstat;
    try {
      const {
        name, surname, email, password,
      } = credentials;
      const sessionId = getOrCreateSessionId(); // Ensure a session id reaches the backend for attribution
      const timezone = await detectTimezone(); // Get user timezone with multiple fallbacks
      regstat = await dispatch(registerAction({
        name, surname, email, password, ip, sessionId, timezone, captchaToken,
      }));
      if (regstat.error) {
        setLoader(false);
        // Reset captcha — each token is single-use; the user must re-solve to retry.
        captchaRef.current?.resetCaptcha();
        setCaptchaToken('');
        setMessage(regstat.error.message === 'db record already exist' ? 'email already exist' : regstat.error.message);
        setButtonTxt('Register');
        // `code` is HttpError.statusCode, mirrored so RTK's error serialization keeps it.
        const errorCode = regstat.error.code ?? 0;
        const reason = signupErrorReason(regstat.error.message);
        if (reason === 'disposable_email') {
          // A legitimate refusal, deliberately kept out of the technical-error bucket.
          sendMetrics('disposable_emails', { error_code: errorCode });
          return;
        }
        sendMetrics('sign_up_common_error', {
          error_code: errorCode,
          error_reason: reason,
          field: reason.startsWith('email') ? 'email' : 'form',
        });
        return;
      }
      setButtonTxt('Redirecting...');
      sendMetrics('sign_up', {
        method: 'onSite',
        // 'oauth' when the visitor came from an MCP connector consent request.
        flow: authFlowOf(from),
      });
      // The journey continues from the confirmation email, usually in a new
      // tab: keep the destination where that tab can find it.
      rememberReturnPath(from);
      route.push(`/auth-account-created/${credentials.email}`);
    } catch (err) {
      setLoader(false);
      setButtonTxt('Register');
      const reason = signupErrorReason(err?.message);
      if (reason === 'disposable_email') {
        sendMetrics('disposable_emails', { error_code: err?.statusCode ?? 0 });
        return;
      }
      sendMetrics('sign_up_common_error', {
        error_code: err?.statusCode ?? 0,
        error_reason: reason === 'unknown' ? 'thrown_exception' : reason,
        field: 'form',
      });
    }
  };

  const error = message ? <ErrorMessage text={message} /> : null;
  const submitError = subError ? <ErrorMessage text={subError} onDismiss={onClear} /> : null;

  return (
    <>
      <div className="back-to-home">
        <Link href="/" className="back-button btn btn-icon btn-primary">
          <i>
            <FeatherIcon icon="arrow-left" className="icons" />
          </i>
        </Link>
      </div>
      <section
        className="bg-home d-flex align-items-center position-relative"
        style={{ backgroundImage: `url(${shape.src}) center` }}
      >
        <Container>
          <Row>
            <Col xs={12}>
              <div className="form-signin p-4 rounded shadow">
                <Form className="form" onSubmit={onSubmit}>
                  <Link href="/">
                    <Image
                      src={logoIcon}
                      width={65}
                      height={65}
                      className="avatar avatar-small mb-4 d-block mx-auto"
                      alt="logo"
                    />
                  </Link>
                  <h5 className="mb-3 text-center">Register your account</h5>
                  <FormGroup>
                    <div className="form-floating mb-2">
                      <Input
                        type="text"
                        className="form-control"
                        id="name"
                        placeholder="Harry"
                        invalid={nInvalid}
                        value={credentials.name}
                        onChange={onHandleChange}
                        onBlur={nameOnBlur}
                      />
                      <Label for="floatingInput">First Name</Label>
                      <FormFeedback>
                        Oh no! Name field is empty.
                      </FormFeedback>
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <div className="form-floating mb-2">
                      <Input
                        type="text"
                        className="form-control"
                        id="surname"
                        placeholder="Potter"
                        invalid={sInvalid}
                        value={credentials.surname}
                        onChange={onHandleChange}
                        onBlur={surnameOnBlur}
                      />
                      <Label for="floatingInput">Second Name</Label>
                      <FormFeedback>
                        Oh no! Surname field is empty.
                      </FormFeedback>
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <div className="form-floating mb-2">
                      <Input
                        type="email"
                        className="form-control"
                        id="email"
                        placeholder="name@example.com"
                        invalid={eInvalid}
                        value={credentials.email}
                        onChange={onHandleChange}
                        onBlur={emailOnBlur}
                      />
                      <Label for="floatingEmail">Email Address</Label>
                      <FormFeedback>
                        Oh no! Email is not valid.
                      </FormFeedback>
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <div className="form-floating mb-3">
                      <Input
                        type="password"
                        className="form-control"
                        id="password"
                        placeholder="Password"
                        invalid={pInvalid}
                        value={credentials.password}
                        onChange={onHandleChange}
                        onBlur={passOnBlur}
                      />
                      <Label for="floatingPassword">Password</Label>
                      <FormFeedback>
                        Password must be at least 6 characters long!
                      </FormFeedback>
                    </div>
                  </FormGroup>

                  <FormGroup className="position-relative" check required>
                    <div className="form-check mb-3 position-relative">
                      <Input className="form-check-input" type="checkbox" value="" id="flexCheckDefault" required />
                      <Label className="form-check-label" for="flexCheckDefault">
                        I Accept
                        {' '}
                        <Link href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary">Terms And Condition</Link>
                      </Label>
                    </div>
                  </FormGroup>

                  <FormGroup className="mb-3 d-flex justify-content-center">
                    <HCaptcha
                      ref={captchaRef}
                      sitekey={process.env.NEXT_PUBLIC_HCAPTCHA_SITEKEY}
                      onVerify={(token) => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken('')}
                      onError={() => setCaptchaToken('')}
                    />
                  </FormGroup>

                  <button className="btn btn-primary w-100" type="submit">
                    {loader ? <span className="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" /> : null}
                    {' '}
                    {buttonTxt}
                  </button>

                  {error}
                  {submitError}

                  <div className="col-12 text-center mt-3">
                    <p className="mb-0 mt-3">
                      <small className="text-dark me-2">Already have an account ?</small>
                      {' '}
                      <Link href={from ? `/auth-login?from=${encodeURIComponent(from)}` : '/auth-login'} className="text-dark fw-bold">Sign in</Link>
                    </p>
                  </div>

                  <p className="mb-0 text-muted mt-3 text-center" suppressHydrationWarning>
                    ©
                    {new Date().getFullYear()}
                    {' '}
                    {' '}
                    svghmi.pro
                  </p>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AuthSignup;
